package com.yendo.mx.ui.cart

import androidx.compose.animation.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.models.DireccionGuardada
import com.yendo.mx.ui.components.SelectorDireccionSheet
import com.yendo.mx.ui.components.MapaSeleccionScreen
import com.yendo.mx.ui.components.solicitarUbicacionGPS
import com.yendo.mx.models.PedidosManager
import com.yendo.mx.models.CartManager
import com.yendo.mx.utils.ConfigManager
import com.yendo.mx.utils.EstadoCarga
import com.yendo.mx.repository.DireccionesRepository
import com.yendo.mx.utils.LogisticaUtils
import com.yendo.mx.utils.formatPrecio
import com.yendo.mx.utils.getAhora
import kotlinx.coroutines.launch
import kotlin.math.*
import kotlin.time.Duration.Companion.days

// IMPORTS PARA FECHAS Y HORARIOS (KMP)
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.toInstant
import kotlinx.datetime.DayOfWeek

import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import coil3.size.Size
import com.yendo.mx.models.CartItem
import com.yendo.mx.models.Producto

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.where

import androidx.compose.ui.draw.clip

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CheckoutScreen(
    onBack: () -> Unit,
    total: Double,
    direccionActual: String,
    latitudCliente: Double,
    longitudCliente: Double,
    metodosBloqueadosCliente: String,
    // 🚀 NUEVOS PARÁMETROS PARA COMERCIOS
    accionAgotado: String,
    pagoPostArmado: Boolean,
    onPedidoConfirmado: (String) -> Unit
) {
    // 🚀 CARGAMOS LAS TARJETAS GUARDADAS DEL USUARIO
    val usuarioActual by com.yendo.mx.repository.AuthRepository.usuarioActual.collectAsState()
    val usuarioId = usuarioActual?.uid
    val tarjetasGuardadas = remember { mutableStateListOf<com.yendo.mx.ui.profile.TarjetaGuardada>() }
    var tarjetaSeleccionada by remember { mutableStateOf<com.yendo.mx.ui.profile.TarjetaGuardada?>(null) }
    var mostrarSelectorTarjetas by rememberSaveable { mutableStateOf(false) }

    LaunchedEffect(usuarioActual) {
        val uid = usuarioActual?.uid
        if (uid != null) {
            dev.gitlive.firebase.Firebase.firestore
                .collection("Usuarios").document(uid).collection("Tarjetas")
                .get().let { snapshot ->
                    val tarjetas = snapshot.documents.map { it.data<com.yendo.mx.ui.profile.TarjetaGuardada>().copy(id = it.id) }
                    tarjetasGuardadas.addAll(tarjetas)
                    if (tarjetas.size == 1) tarjetaSeleccionada = tarjetas.first()
                }
        }
    }

    // 🚀 SINCRONIZACIÓN EN TIEMPO REAL: Evita datos obsoletos de horarios o cierres de emergencia
    LaunchedEffect(CartManager.restauranteActualId) {
        val idRest = CartManager.restauranteActualId
        if (idRest != null) {
            try {
                val snapshot = Firebase.firestore
                    .collection("Negocios")
                    .document(idRest)
                    .get()
                
                if (snapshot.exists) {
                    val restActualizado = snapshot.data<com.yendo.mx.models.RestauranteFirebase>().copy(id = snapshot.id)
                    CartManager.sincronizarMetadata(restActualizado)
                    println("YENDO_LOG: Metadata del restaurante $idRest sincronizada en Checkout.")
                }

                // 🚀 2. SINCRONIZACIÓN DE PRODUCTOS (PRECIOS Y ESCALAS)
                val idsProductos = CartManager.items.map { it.producto.id }.distinct()
                if (idsProductos.isNotEmpty()) {
                    val productsSnapshot = Firebase.firestore
                        .collection("Productos")
                        .where { dev.gitlive.firebase.firestore.FieldPath.documentId inArray idsProductos }
                        .get()
                    
                    val freshProducts = productsSnapshot.documents.map { 
                        it.data<com.yendo.mx.models.Producto>().copy(id = it.id) 
                    }
                    CartManager.sincronizarProductos(freshProducts)
                    println("YENDO_LOG: ${freshProducts.size} productos sincronizados en Checkout.")
                }

            } catch (e: Exception) {
                println("YENDO_LOG ERROR: Fallo al sincronizar en Checkout -> ${e.message}")
            }
        }
    }

    val isDark = isSystemInDarkTheme()
    val colorFondoPantalla = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorFondoTarjeta = if (isDark) Color(0xFF1E1E1E) else Color.White
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray
    val colorBordeCard = if (isDark) Color(0xFF2C2C2E) else Color(0xFFEEEEEE)

    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val uriHandler = androidx.compose.ui.platform.LocalUriHandler.current

    val estadoConfig by ConfigManager.estadoConfig.collectAsState()
    val zonaConfig = (estadoConfig as? EstadoCarga.Exito)?.config
    val finanzasZona = zonaConfig?.finanzas_cliente

    val tiempoActualMs = remember { getAhora() }
    val pausaHasta = CartManager.restauranteActualPausaHasta ?: 0L
    val estaEnPanico = CartManager.restauranteActualPausadoManualmente == true && pausaHasta > tiempoActualMs

    val horariosRestaurante = CartManager.restauranteActualHorarios
    val zonaHoraria = TimeZone.currentSystemDefault()
    val ahora = remember { Instant.fromEpochMilliseconds(getAhora()).toLocalDateTime(zonaHoraria) }

    val estaAbiertoAhora = remember(horariosRestaurante) {
        if (horariosRestaurante == null) return@remember true
        val diaActual = ahora.dayOfWeek
        val horaActualStr = "${ahora.hour.toString().padStart(2, '0')}:${ahora.minute.toString().padStart(2, '0')}"

        val franjasDeHoy = when(diaActual) {
            DayOfWeek.MONDAY -> horariosRestaurante.lunes
            DayOfWeek.TUESDAY -> horariosRestaurante.martes
            DayOfWeek.WEDNESDAY -> horariosRestaurante.miercoles
            DayOfWeek.THURSDAY -> horariosRestaurante.jueves
            DayOfWeek.FRIDAY -> horariosRestaurante.viernes
            DayOfWeek.SATURDAY -> horariosRestaurante.sabado
            DayOfWeek.SUNDAY -> horariosRestaurante.domingo
            else -> emptyList()
        }

        val franjasActivas = franjasDeHoy.filter { it.activo }.sortedBy { it.abre }
        franjasActivas.any { horaActualStr >= it.abre && horaActualStr <= it.cierra }
    }

    var tipoPedido by rememberSaveable { mutableStateOf("") } // Declarado antes para que los banners lo usen

    val permiteDelivery = CartManager.restauranteActualDeliveryActivo ?: true
    val permitePickup = (zonaConfig?.logistica?.pickup_activo ?: true) && (CartManager.restauranteActualPickupActivo ?: true)
    val globalProgramadoActivo = zonaConfig?.operacion?.programados_activos ?: true
    val permiteProg = globalProgramadoActivo && (CartManager.restauranteActualProgramadoActivo ?: false)

    val deliveryYendoAsapOk = (zonaConfig?.logistica?.delivery_activo ?: true) && LogisticaUtils.estaPlataformaAbierta(zonaConfig)
    val permiteDeliveryASAP = estaAbiertoAhora && (deliveryYendoAsapOk || (CartManager.restauranteActualDeliveryPropioActivo ?: false))

    var tipoTiempo by rememberSaveable { mutableStateOf(if (!permiteDeliveryASAP && permiteProg) "Programado" else "ASAP") }
    
    // Inicialización del tipo de pedido
    LaunchedEffect(Unit) {
        if (tipoPedido.isEmpty()) {
            tipoPedido = if (permiteDelivery) "Delivery" else if (permitePickup) "Pickup" else ""
        }
    }

    LaunchedEffect(permiteDelivery, permitePickup, permiteProg, permiteDeliveryASAP) {
        if (tipoPedido == "Delivery" && !permiteDelivery) tipoPedido = if (permitePickup) "Pickup" else ""
        if (tipoPedido == "Pickup" && !permitePickup) tipoPedido = if (permiteDelivery) "Delivery" else ""
        if (tipoTiempo == "ASAP" && !permiteDeliveryASAP && permiteProg) tipoTiempo = "Programado"
    }

    val hoyStr = "Hoy"
    val mananaStr = "Mañana"
    var diaProgramado by rememberSaveable { mutableStateOf(hoyStr) }
    var horaProgramada by rememberSaveable { mutableStateOf("") }

    val horasDisponibles = remember(diaProgramado, horariosRestaurante, zonaConfig) {
        if (horariosRestaurante == null || zonaConfig == null) return@remember emptyList<String>()

        val fechaObjetivo = if (diaProgramado == mananaStr) {
            Instant.fromEpochMilliseconds(getAhora() + 1.days.inWholeMilliseconds).toLocalDateTime(zonaHoraria)
        } else {
            ahora
        }

        // 1. Obtener horario de la Plataforma para el día objetivo
        val nombreDiaEsp = LogisticaUtils.obtenerNombreDiaEspanol(fechaObjetivo.dayOfWeek)
        val horarioPlataformaHoy = zonaConfig.operacion.horario_semanal[nombreDiaEsp]
        if (horarioPlataformaHoy?.activo != true) return@remember emptyList<String>()

        val platAbreMin = LogisticaUtils.aMinutos(horarioPlataformaHoy.apertura)
        val platCierraMin = LogisticaUtils.aMinutos(horarioPlataformaHoy.cierre)

        // 2. Obtener franjas del Restaurante
        val franjas = when (fechaObjetivo.dayOfWeek) {
            DayOfWeek.MONDAY -> horariosRestaurante.lunes
            DayOfWeek.TUESDAY -> horariosRestaurante.martes
            DayOfWeek.WEDNESDAY -> horariosRestaurante.miercoles
            DayOfWeek.THURSDAY -> horariosRestaurante.jueves
            DayOfWeek.FRIDAY -> horariosRestaurante.viernes
            DayOfWeek.SATURDAY -> horariosRestaurante.sabado
            DayOfWeek.SUNDAY -> horariosRestaurante.domingo
        }

        val listaHoras = mutableListOf<String>()
        val horaActualMinutos = ahora.hour * 60 + ahora.minute + 60 // 🚀 CAMBIO: 60 min de anticipación (1 hora)

        franjas.filter { it.activo }.forEach { franja ->
            val restAbreParts = franja.abre.split(":")
            val restCierraParts = franja.cierra.split(":")

            if (restAbreParts.size == 2 && restCierraParts.size == 2) {
                val restAbreMin = restAbreParts[0].toInt() * 60 + restAbreParts[1].toInt()
                val restCierraMin = restCierraParts[0].toInt() * 60 + restCierraParts[1].toInt()

                // Intersección: Máximo de las aperturas y Mínimo de los cierres
                var actualMin = maxOf(restAbreMin, platAbreMin)
                val finalCierreMin = minOf(restCierraMin, platCierraMin)

                // Si es hoy, debemos empezar al menos 1 hora después de ahora
                if (diaProgramado == hoyStr) {
                    actualMin = maxOf(actualMin, horaActualMinutos)
                }

                // Redondear actualMin al siguiente bloque de 30 mins
                actualMin = if (actualMin % 30 == 0) actualMin else ((actualMin / 30) + 1) * 30

                while (actualMin <= finalCierreMin) {
                    // Si es hoy, validar contra la hora actual + 60 mins (ya manejado en actualMin arriba)
                    if (diaProgramado == mananaStr || actualMin >= horaActualMinutos) {
                        val h = actualMin / 60
                        val m = actualMin % 60
                        listaHoras.add("${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}")
                    }
                    actualMin += 30
                }
            }
        }
        listaHoras.distinct().sorted()
    }

    // 🚀 LÓGICA DE DISPONIBILIDAD MOVIDA AQUÍ PARA TENER ACCESO A TODAS LAS VARIABLES
    val plataformaAbierta = remember(zonaConfig) { LogisticaUtils.estaPlataformaAbierta(zonaConfig) }

    val disponibilidadOk = remember(plataformaAbierta, estaAbiertoAhora, tipoPedido, tipoTiempo, horaProgramada, horasDisponibles, permiteDeliveryASAP) {
        if (tipoTiempo == "Programado") {
            // Para pedidos programados, si hay una hora seleccionada y es parte de la lista válida (ya intersectada), está OK
            permiteProg && horaProgramada.isNotBlank() && horasDisponibles.contains(horaProgramada)
        } else {
            // Para pedidos al momento (ASAP), si es Pickup valida que esté abierto el negocio; si es Delivery valida permiteDeliveryASAP
            if (tipoPedido == "Pickup") estaAbiertoAhora
            else permiteDeliveryASAP
        }
    }

    val mensajeAlerta = when {
        tipoTiempo == "ASAP" && !estaAbiertoAhora -> "El establecimiento se encuentra cerrado en este momento. Intenta programar tu pedido."
        else -> null
    }

    LaunchedEffect(horasDisponibles, diaProgramado) {
        if (horasDisponibles.isNotEmpty()) {
            if (!horasDisponibles.contains(horaProgramada)) {
                horaProgramada = horasDisponibles.first()
            }
        } else if (diaProgramado == hoyStr) {
            diaProgramado = mananaStr
        } else {
            horaProgramada = ""
        }
    }

    var cuponInput by rememberSaveable { mutableStateOf("") }
    var descuentoAplicado by rememberSaveable { mutableStateOf(0.0) }
    var patrocinadorCupon by rememberSaveable { mutableStateOf("") }
    var mensajeCupon by rememberSaveable { mutableStateOf("") }
    var procesandoPedido by rememberSaveable { mutableStateOf(false) }

    val latGPSReal by ConfigManager.latGPSReal.collectAsState()
    val lngGPSReal by ConfigManager.lngGPSReal.collectAsState()

    var direccionCheckout by rememberSaveable { mutableStateOf(direccionActual) }
    var latitudCheckout by rememberSaveable { mutableStateOf(latitudCliente) }
    var longitudCheckout by rememberSaveable { mutableStateOf(longitudCliente) }
    // 🚀 NUEVAS VARIABLES DE MEMORIA LOGÍSTICA
    var tituloCheckout by rememberSaveable { mutableStateOf("") }
    var tipoUbicacionCheckout by rememberSaveable { mutableStateOf("Casa") }
    var opcionEntregaCheckout by rememberSaveable { mutableStateOf("Entregar en puerta") }
    var indicacionesCheckout by rememberSaveable { mutableStateOf("") }

    var mostrarAlertaDireccionLejana by rememberSaveable { mutableStateOf(false) }

    var cambiarDir by rememberSaveable { mutableStateOf(false) }
    var mostrarMapaCheckout by rememberSaveable { mutableStateOf(false) }
    var forzarGpsCheckout by rememberSaveable { mutableStateOf(false) }
    var indiceEdicion by rememberSaveable { mutableStateOf(-1) }
    var direccionPrevia by rememberSaveable { mutableStateOf("") }

    var mostrarDialogoTarifa by rememberSaveable { mutableStateOf(false) }

    val hayAgotados = CartManager.items.any { 
        !it.producto.disponible || it.adicionales.any { adic -> !adic.disponible } 
    }

    // 🚀 RE-VALIDACIÓN DE CUPÓN SI EL TOTAL CAMBIA
    LaunchedEffect(total) {
        if (descuentoAplicado > 0.0) {
            descuentoAplicado = 0.0
            patrocinadorCupon = ""
            mensajeCupon = "El total de tu carrito cambió. Vuelve a aplicar tu cupón."
        }
    }

    // 🚀 LÓGICA DE CVV PARA TARJETAS GUARDADAS
    var mostrarDialogoCVV by rememberSaveable { mutableStateOf(false) }
    var cvvInput by rememberSaveable { mutableStateOf("") }
    var procesandoCVV by rememberSaveable { mutableStateOf(false) }

    val coordenadasValidas = latitudCheckout != 0.0 && longitudCheckout != 0.0 &&
            !direccionCheckout.contains("Permiso denegado", ignoreCase = true) &&
            !direccionCheckout.contains("Buscando", ignoreCase = true) &&
            !direccionCheckout.contains("Selecciona", ignoreCase = true)

    val estaCargandoGps = remember { mutableStateOf(false) }
    val dispararGps = solicitarUbicacionGPS { direccionDetectada, lat, lng ->
        direccionCheckout = direccionDetectada
        latitudCheckout = lat
        longitudCheckout = lng
        estaCargandoGps.value = false
        cambiarDir = false
    }

    val direccionesState = DireccionesRepository.observarMisDirecciones().collectAsState(initial = emptyList())
    val listaDirecciones = direccionesState.value.sortedByDescending { it.ultimaVezUsada }
    LaunchedEffect(listaDirecciones, direccionCheckout) {
        val dirGuardada = listaDirecciones.firstOrNull { it.direccion == direccionCheckout }
        if (dirGuardada != null) {
            tipoUbicacionCheckout = dirGuardada.tipoUbicacion
            opcionEntregaCheckout = dirGuardada.opcionEntrega
            indicacionesCheckout = dirGuardada.indicaciones
            tituloCheckout = dirGuardada.titulo // 🚀 RECUPERAMOS EL NOMBRE PERSONALIZADO
        } else {
            // Si por alguna razón usa el GPS en vivo y no está guardada, limpiamos los campos
            tipoUbicacionCheckout = "Casa"
            opcionEntregaCheckout = "Entregar en puerta"
            indicacionesCheckout = ""
            tituloCheckout = "" // 🚀 LIMPIAMOS SI ES DIRECCIÓN NUEVA
        }
    }

    val costoEnvio = remember(latitudCheckout, longitudCheckout, tipoPedido, zonaConfig) {
        if (tipoPedido == "Pickup") 0.0
        else if (zonaConfig == null) -1.0
        else {
            val latRestaurante = CartManager.restauranteActualLat
            val lngRestaurante = CartManager.restauranteActualLng
            val distanciaBase = calcularDistanciaKm(latRestaurante, lngRestaurante, latitudCheckout, longitudCheckout)
            calcularCostoEnvioDin(
                distanciaLineaRectaKm = distanciaBase,
                tarifaInicial = finanzasZona?.tarifa_inicial?.toDouble() ?: 5.0,
                costoPorKm = finanzasZona?.costo_km?.toDouble() ?: 10.0,
                envioMinimo = finanzasZona?.envio_minimo?.toDouble() ?: 15.0
            )
        }
    }

    val kmMaximos = zonaConfig?.logistica_radios?.entrega_maxima_km?.toDouble() ?: 5.0
    val distanciaActual = calcularDistanciaKm(CartManager.restauranteActualLat, CartManager.restauranteActualLng, latitudCheckout, longitudCheckout)
    val fueraDeRango = tipoPedido == "Delivery" && distanciaActual > kmMaximos && coordenadasValidas

    val propinaRecomendada = remember(total) { minOf(100.0, maxOf(10.0, kotlin.math.round(total * 0.10))) }
    var propinaSeleccionada by rememberSaveable { mutableStateOf(propinaRecomendada) }

    LaunchedEffect(tipoPedido) {
        if (tipoPedido == "Pickup") propinaSeleccionada = 0.0 else propinaSeleccionada = propinaRecomendada
    }
// 🚀 NUEVA LÓGICA DE TARIFA HÍBRIDA (Fija + Porcentaje)
    val tarifaServicioFija = finanzasZona?.tarifa_servicio_fija?.toDouble() ?: 0.0
    val tarifaServicioPorcentaje = finanzasZona?.tarifa_servicio_porcentaje?.toDouble() ?: 0.0
    val tarifaServicio = tarifaServicioFija + (total * tarifaServicioPorcentaje)

    // 🚀 Extraemos el Pedido Mínimo del Restaurante
    val pedidoMinimoRestaurante = CartManager.restauranteActualPedidoMinimo ?: 0.0
    val noAlcanzaMinimo = total < pedidoMinimoRestaurante // 'total' es el subtotal de los productos

    val costoEnvioReal = if (costoEnvio == -1.0) 0.0 else costoEnvio

    val totalPagar = maxOf(0.0, (total + costoEnvioReal + tarifaServicio + propinaSeleccionada) - descuentoAplicado)

    // 🚀 LÓGICA DE BLOQUEO DE MÉTODOS DE PAGO DEL CLIENTE
    val metodosZona = finanzasZona?.metodos_pago_activos ?: listOf("Efectivo", "Tarjeta", "Transferencia")
    val metodosRestaurante = CartManager.restauranteActualMetodosPago ?: listOf("Efectivo")

    val metodosBloqueadosList = remember(metodosBloqueadosCliente) {
        if (metodosBloqueadosCliente.isBlank()) emptyList()
        else metodosBloqueadosCliente
            .replace("\"", "")
            .replace("'", "")
            .split(",")
            .map { it.trim().lowercase() }
    }

    val deudaRestaurante = CartManager.restauranteActualDeuda ?: 0.0
    val limitePersonalizado = CartManager.restauranteActualLimiteDeuda
    val limiteGlobal = zonaConfig?.finanzas_operativas?.max_deuda_negocio?.toDouble() ?: 2000.0
    val limiteAplicable = limitePersonalizado ?: limiteGlobal
    val restauranteExcedido = (deudaRestaurante >= limiteAplicable) || (CartManager.restauranteActualBalanceExcedido == true)

    val metodosFinales = remember(metodosZona, metodosRestaurante, totalPagar, zonaConfig, restauranteExcedido, metodosBloqueadosList) {
        val disponibles = metodosZona.intersect(metodosRestaurante.toSet()).toMutableList()

        println("YENDO_LOG CHECKOUT: metodosZona = $metodosZona")
        println("YENDO_LOG CHECKOUT: metodosRestaurante = $metodosRestaurante")
        println("YENDO_LOG CHECKOUT: disponibles tras intersección = $disponibles")
        println("YENDO_LOG CHECKOUT: metodosBloqueadosCliente = $metodosBloqueadosList")

        val disponiblesLimpio = disponibles.filter { metodoOriginal ->
            val metodoLimpio = metodoOriginal.trim().lowercase()
            !metodosBloqueadosList.contains(metodoLimpio)
        }.toMutableList()

        val maxEfectivo = zonaConfig?.prevencion_fraude?.maximo_efectivo_pedido?.toDouble() ?: Double.MAX_VALUE
        val requeridosTarjeta = zonaConfig?.prevencion_fraude?.pedidos_tarjeta_requeridos?.toInt() ?: 0

        if (totalPagar > maxEfectivo || 0 < requeridosTarjeta || restauranteExcedido) {
            println("YENDO_LOG CHECKOUT: Removiendo Efectivo. maxEfectivo=$maxEfectivo, reqTarjeta=$requeridosTarjeta, restauranteExcedido=$restauranteExcedido")
            disponiblesLimpio.remove("Efectivo")
        }

        val bancoRest = CartManager.restauranteActualBanco ?: ""
        val cuentaRest = CartManager.restauranteActualCuenta ?: ""
        if (restauranteExcedido || bancoRest.isBlank() || cuentaRest.isBlank()) {
            println("YENDO_LOG CHECKOUT: Removiendo Transferencia. restauranteExcedido=$restauranteExcedido, bancoRest='$bancoRest', cuentaRest='$cuentaRest'")
            disponiblesLimpio.remove("Transferencia")
        }

        val listaFinal = disponiblesLimpio.toList().ifEmpty { listOf("Tarjeta") }
        println("YENDO_LOG CHECKOUT: metodosFinales resultado = $listaFinal")
        listaFinal
    }

    var metodoPago by rememberSaveable { mutableStateOf(metodosFinales.firstOrNull() ?: "Tarjeta") }

    // 🚀 FIX: OBLIGAMOS A EXPULSAR EL EFECTIVO SI LLEGA EL BLOQUEO
    LaunchedEffect(metodosFinales) {
        if (!metodosFinales.contains(metodoPago)) {
            metodoPago = metodosFinales.firstOrNull() ?: "Tarjeta"
        }
    }

    var cambioEfectivoSeleccionado by rememberSaveable { mutableStateOf<Double?>(null) }
    LaunchedEffect(metodoPago, totalPagar) { cambioEfectivoSeleccionado = null }
    LaunchedEffect(metodoPago) {
        if (descuentoAplicado > 0.0) {
            descuentoAplicado = 0.0
            patrocinadorCupon = ""
            mensajeCupon = "Método de pago cambiado. Vuelve a aplicar tu cupón."
        }
    }

    val realizarPedido = { tokenCertificado: String?, issuerIdAlternativo: String? ->
        scope.launch {
            val currentMs = getAhora()
            procesandoPedido = true
            val cambioAEntregarCalc = if (metodoPago == "Efectivo" && cambioEfectivoSeleccionado != null) {
                val dif = cambioEfectivoSeleccionado!! - totalPagar
                if (dif > 0) kotlin.math.round(dif * 100.0) / 100.0 else 0.0
            } else null

            // ... (Lógica de fechas programadas intacta) ...
            val fechaFinalProgramada = if (tipoTiempo == "Programado") {
                val fechaBase = if (diaProgramado == mananaStr) Instant.fromEpochMilliseconds(getAhora() + 1.days.inWholeMilliseconds) else Instant.fromEpochMilliseconds(getAhora())
                val horaParts = horaProgramada.split(":")
                if (horaParts.size == 2) {
                    val localDt = fechaBase.toLocalDateTime(zonaHoraria)
                    kotlinx.datetime.LocalDateTime(localDt.year, localDt.monthNumber, localDt.dayOfMonth, horaParts[0].toInt(), horaParts[1].toInt()).toInstant(zonaHoraria).toEpochMilliseconds()
                } else currentMs
            } else null

            val idGenerado = PedidosManager.enviarPedido(
                tipoPedido = tipoPedido.uppercase(),
                idZona = CartManager.restauranteActualIdZona?.takeIf { it.isNotBlank() } 
                    ?: zonaConfig?.id_zona?.takeIf { it.isNotBlank() } 
                    ?: "arriaga",
                esProgramado = tipoTiempo == "Programado",
                fechaProgramada = fechaFinalProgramada,
                subtotal = total,
                costoEnvio = costoEnvio,
                tarifaServicio = tarifaServicio,
                propina = propinaSeleccionada,
                descuento = descuentoAplicado,
                totalPagado = totalPagar,
                metodoPago = metodoPago,
                conCuantoPaga = if (metodoPago == "Efectivo") cambioEfectivoSeleccionado else null,
                cambioAEntregar = cambioAEntregarCalc,
                nombreRestaurante = CartManager.restauranteActualNombre ?: "Negocio Yendo",
                logoRestaurante = CartManager.restauranteActualLogo ?: "",
                direccionNegocio = CartManager.restauranteActualDireccion ?: "",
                latNegocio = CartManager.restauranteActualLat,
                lngNegocio = CartManager.restauranteActualLng,
                direccionEntrega = direccionCheckout,
                latitud = latitudCheckout,
                longitud = longitudCheckout,
                tipoUbicacion = tipoUbicacionCheckout,
                opcionEntrega = opcionEntregaCheckout,
                indicaciones = indicacionesCheckout,
                accionAgotado = accionAgotado,
                pagoPostArmado = pagoPostArmado,
                idCuponUsado = if (descuentoAplicado > 0.0) cuponInput else null,
                idTarjetaSeleccionada = tarjetaSeleccionada?.tokenPago,
                tokenPagar = tokenCertificado,
                requierePreAutorizacion = pagoPostArmado,
                nombreCliente = usuarioActual?.nombre ?: "",
                apellidoCliente = usuarioActual?.apellido ?: "",
                emailCliente = usuarioActual?.email ?: "",
                telefonoCliente = usuarioActual?.telefono ?: "",
                issuerId = issuerIdAlternativo ?: tarjetaSeleccionada?.issuerId
            )

            procesandoPedido = false
            if (idGenerado != null) {
                onPedidoConfirmado(idGenerado)
            } else {
                snackbarHostState.showSnackbar("Error al crear el pedido. Intenta de nuevo.")
            }
        }
    }

    val procesarPagoConCVV = {
        if (cvvInput.length < 3) {
            scope.launch { snackbarHostState.showSnackbar("Ingresa un CVV válido") }
        } else {
            procesandoCVV = true
            scope.launch {
                try {
                    // 🚀 ASEGURAMOS USAR 'tokenPago' (El Card ID de MP)
                    val cardId = tarjetaSeleccionada?.tokenPago ?: ""
                    val issuerId = tarjetaSeleccionada?.issuerId
                    val nuevoToken = com.yendo.mx.repository.MercadoPagoRepository.generarTokenConTarjetaGuardada(cardId, cvvInput)

                    cvvInput = "" 

                    if (nuevoToken != null) {
                        mostrarDialogoCVV = false
                        // 🚀 IMPORTANTE: Llamamos a realizar pedido.
                        // Enviamos el nuevoToken como 'tokenCertificado'
                        realizarPedido(nuevoToken, issuerId)
                    } else {
                        snackbarHostState.showSnackbar("No se pudo validar la tarjeta. Revisa tu CVV.")
                    }
                } catch (e: Exception) {
                    cvvInput = ""
                    snackbarHostState.showSnackbar("Error al procesar pago: ${e.message}")
                } finally {
                    procesandoCVV = false
                }
            }
        }
    }

    if (mostrarMapaCheckout) {
        MapaSeleccionScreen(
            direccionInicial = direccionPrevia,
            buscarGpsAlAbrir = forzarGpsCheckout,
            onBack = { 
                mostrarMapaCheckout = false
                forzarGpsCheckout = false 
                // 🚀 Restaura el estado a la última dirección válida del checkout
                scope.launch { ConfigManager.procesarUbicacion(latitudCheckout, longitudCheckout, "") }
            },
            onConfirmar = { etiqueta, nuevaDir, lat, lng, cp, tipo, opcion, notas -> // 🚀 Recibimos los 8 datos
                val idExistente = listaDirecciones.getOrNull(indiceEdicion)?.id ?: ""

                // 🚀 Creamos el objeto con TODO lo nuevo
                val nuevaDireccion = DireccionGuardada(
                    id = idExistente,
                    titulo = etiqueta,
                    direccion = nuevaDir,
                    lat = lat,
                    lng = lng,
                    cp = cp,
                    tipoUbicacion = tipo,
                    opcionEntrega = opcion,
                    indicaciones = notas,
                    ultimaVezUsada = getAhora()
                )

                scope.launch { DireccionesRepository.guardarDireccion(nuevaDireccion) }

                // 🚀 Sincronizamos con el estado de la pantalla actual
                direccionCheckout = nuevaDir
                latitudCheckout = lat
                longitudCheckout = lng
                tipoUbicacionCheckout = tipo
                opcionEntregaCheckout = opcion
                indicacionesCheckout = notas
                tituloCheckout = etiqueta // 🚀 GUARDAMOS LA ETIQUETA AQUÍ TAMBIÉN

                forzarGpsCheckout = false
                mostrarMapaCheckout = false
                indiceEdicion = -1
                cambiarDir = false
            }
        )
    } else {
        Scaffold(
            containerColor = colorFondoPantalla,
            snackbarHost = { SnackbarHost(snackbarHostState) },
            topBar = {
                Column(modifier = Modifier.statusBarsPadding()) { // 🚀 FIX: Respeta la barra de estado
                    // 🚀 BANNER DE ALERTA OPERATIVA (Sincronizado con VistaEnvios)
                    AnimatedVisibility(visible = mensajeAlerta != null) {
                        Surface(
                            modifier = Modifier.fillMaxWidth(),
                            color = if(isDark) Color(0xFF331111) else Color(0xFFFFEBEE)
                        ) {
                            Row(
                                modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(Icons.Default.ErrorOutline, null, tint = Color.Red, modifier = Modifier.size(18.dp))
                                Spacer(Modifier.width(12.dp))
                                Text(
                                    mensajeAlerta ?: "",
                                    color = if(isDark) Color.White else Color.Red,
                                    fontSize = 13.sp,
                                    fontWeight = FontWeight.Bold,
                                    modifier = Modifier.weight(1f)
                                )
                            }
                        }
                    }

                    TopAppBar(
                        title = { 
                            Text(
                                "Finalizar Pedido", 
                                fontWeight = FontWeight.ExtraBold, 
                                fontSize = 18.sp, // 🚀 REDUCIDO PARA IOS
                                color = colorTextoPrincipal
                            ) 
                        },
                        navigationIcon = { 
                            IconButton(onClick = onBack) { 
                                Icon(Icons.AutoMirrored.Filled.ArrowBack, null, tint = colorTextoPrincipal) 
                            } 
                        },
                        colors = TopAppBarDefaults.topAppBarColors(containerColor = colorFondoTarjeta),
                        windowInsets = WindowInsets(0, 0, 0, 0) // 🚀 EVITA PADDING EXTRA
                    )
                }
            },
            bottomBar = {
                Surface(shadowElevation = 20.dp, color = colorFondoTarjeta) {
                    Column(modifier = Modifier.padding(20.dp).navigationBarsPadding()) {

                        // 🚀 NUEVO: AVISO VISUAL DE PAGO POST-ARMADO (TIENDITAS)
                        if (pagoPostArmado) {
                            Card(modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp), colors = CardDefaults.cardColors(containerColor = if(isDark) Color(0xFF0D253F) else Color(0xFFE3F2FD))) {
                                Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                                    Icon(Icons.Default.Info, null, tint = Color(0xFF1976D2), modifier = Modifier.size(20.dp))
                                    Spacer(Modifier.width(8.dp))
                                    Text("El cobro se realizará hasta que la tienda confirme el stock exacto.", color = Color(0xFF1976D2), fontSize = 12.sp, lineHeight = 14.sp)
                                }
                            }
                        }

                        Row(modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                            Text(if (pagoPostArmado) "Total estimado" else "Total a pagar", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = colorTextoPrincipal)
                            if (costoEnvio == -1.0) {
                                Text("Calculando...", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoSecundario)
                            } else {
                                Text(if (pagoPostArmado) "~ $${formatPrecio(totalPagar)}" else "$${formatPrecio(totalPagar)}", fontWeight = FontWeight.Black, fontSize = 22.sp, color = RosaYendo)
                            }
                        }

                        // 🔥 AVISO VISUAL DE BLOQUEO DE SEGURIDAD
                        if (metodosBloqueadosList.contains("efectivo") && metodosRestaurante.contains("Efectivo")) {
                            Text("El pago en efectivo está deshabilitado para tu cuenta por motivos de seguridad.", color = Color.Red, fontSize = 11.sp, modifier = Modifier.padding(bottom = 8.dp))
                        } else if (!metodosFinales.contains("Efectivo") && metodosRestaurante.contains("Efectivo")) {
                            Text("El pago en efectivo está deshabilitado temporalmente para este pedido.", color = Color.Red, fontSize = 11.sp, modifier = Modifier.padding(bottom = 8.dp))
                        }

                        // 🚀 CANDADO DURO CONTRA COMPRAS NO AUTORIZADAS
                        val intentoPagoEfectivoBloqueado = metodoPago == "Efectivo" && !metodosFinales.contains("Efectivo")
                        // 🚀 NUEVA LÓGICA DE BLOQUEO: Intersección Plataforma + Negocio
                        val botonInvalido = procesandoPedido || costoEnvio == -1.0 || zonaConfig == null || tipoPedido.isBlank() || 
                                           (tipoTiempo == "Programado" && horaProgramada.isBlank()) || 
                                           intentoPagoEfectivoBloqueado || noAlcanzaMinimo || !disponibilidadOk || hayAgotados

                        Button(
                            onClick = {
                                if (!disponibilidadOk) { // 🚀 DOBLE ESCUDO
                                    scope.launch { snackbarHostState.showSnackbar("Lo sentimos, este servicio no está disponible actualmente.") }
                                    return@Button
                                }
                                if (hayAgotados) {
                                    scope.launch { snackbarHostState.showSnackbar("Hay artículos agotados en tu carrito. Regresa para eliminarlos.") }
                                    return@Button
                                }
                                val currentMs = getAhora()
                                val isPanicoRealTime = CartManager.restauranteActualPausadoManualmente == true && (CartManager.restauranteActualPausaHasta ?: 0L) > currentMs

                                if (isPanicoRealTime) {
                                    scope.launch { snackbarHostState.showSnackbar("El restaurante acaba de pausar la recepción de pedidos por saturación. Intenta más tarde.") }
                                    return@Button
                                }
                                if (tipoTiempo == "ASAP" && !estaAbiertoAhora) {
                                    scope.launch { snackbarHostState.showSnackbar("El restaurante acaba de cerrar. Por favor, cambia a pedido Programado.") }
                                    return@Button
                                }
                                if (tipoPedido.isBlank()) {
                                    scope.launch { snackbarHostState.showSnackbar("Este restaurante no tiene métodos de entrega disponibles.") }
                                    return@Button
                                }
                                if (tipoTiempo == "Programado" && horaProgramada.isBlank()) {
                                    scope.launch { snackbarHostState.showSnackbar("Por favor, selecciona una hora válida para tu pedido programado.") }
                                    return@Button
                                }
                                if (noAlcanzaMinimo) {
                                    scope.launch { snackbarHostState.showSnackbar("Faltan $${formatPrecio(pedidoMinimoRestaurante - total)} para alcanzar el mínimo del restaurante.") }
                                    return@Button
                                }

                                // BLOQUEO EXTRA POR SEGURIDAD AL DAR CLIC
                                if (metodoPago == "Efectivo" && !metodosFinales.contains("Efectivo")) {
                                    scope.launch { snackbarHostState.showSnackbar("El pago en efectivo no está permitido para esta cuenta.") }
                                    return@Button
                                }

                                if (zonaConfig == null || costoEnvio == -1.0) {
                                    scope.launch { snackbarHostState.showSnackbar("Calculando tarifas, por favor espera un momento...") }
                                } else if (fueraDeRango) {
                                    scope.launch { snackbarHostState.showSnackbar("Estás fuera del radio de entrega permitido. Intenta acercar el marcador o pasa por él.") }
                                } else if (tipoPedido == "Delivery" && !coordenadasValidas) {
                                    scope.launch { snackbarHostState.showSnackbar("Por favor, selecciona una ubicación válida.") }
                                    cambiarDir = true
                                } else if (metodoPago == "Tarjeta") {
                                    if (tarjetaSeleccionada == null) {
                                        scope.launch { snackbarHostState.showSnackbar("Por favor selecciona una tarjeta para continuar.") }
                                        mostrarSelectorTarjetas = true
                                    } else {
                                        // 🚀 EL CANDADO DEFINITIVO:
                                        // Si el ID es persistente (Corto), pedimos CVV para validar.
                                        // Si por alguna razón es un token (Largo), lo mandamos directo.
                                        val tokenActual = tarjetaSeleccionada?.tokenPago ?: ""
                                        if (tokenActual.length < 25) {
                                            mostrarDialogoCVV = true
                                        } else {
                                            realizarPedido(null, null)
                                        }
                                    }
                                } else if (metodoPago == "Efectivo" && cambioEfectivoSeleccionado == null) {
                                    scope.launch { snackbarHostState.showSnackbar("Selecciona con cuánto vas a pagar en efectivo.") }
                                } else {
                                    // 🚀 VALIDACIÓN DE DISTANCIA REACTIVA
                                    val latReal = ConfigManager.latGPSReal.value
                                    val lngReal = ConfigManager.lngGPSReal.value

                                    if (tipoPedido == "Delivery" && latReal != 0.0 && lngReal != 0.0) {
                                        val distMetros = calcularDistanciaMetros(
                                            lat1 = latReal,
                                            lon1 = lngReal,
                                            lat2 = latitudCheckout,
                                            lon2 = longitudCheckout
                                        )

                                        val limiteMetros = (zonaConfig?.logistica_radios?.limite_alerta_ubicacion_m ?: 500L).toFloat()

                                        if (distMetros > limiteMetros) {
                                            mostrarAlertaDireccionLejana = true
                                        } else {
                                            realizarPedido(null, null)
                                        }
                                    } else {
                                        realizarPedido(null, null)
                                    }
                                }
                            },
                            modifier = Modifier.fillMaxWidth().height(56.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = if (botonInvalido || fueraDeRango || (!coordenadasValidas && tipoPedido == "Delivery")) Color.Gray else RosaYendo),
                            shape = RoundedCornerShape(16.dp),
                            enabled = !botonInvalido
                        ) {
                            if (procesandoPedido) CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp), strokeWidth = 3.dp)
                            else Text(
                                text = if (intentoPagoEfectivoBloqueado) "Efectivo no permitido"
                                else if (tipoPedido.isBlank()) "Métodos de entrega no disponibles"
                                else if (noAlcanzaMinimo) "Agrega $${formatPrecio(pedidoMinimoRestaurante - total)} para pedir"
                                else if (tipoTiempo == "Programado" && horaProgramada.isBlank()) "Horario no disponible"
                                else if (tipoPedido == "Delivery" && !coordenadasValidas) "Ubicación Inválida"
                                else if (fueraDeRango) "Fuera de Rango"
                                else if (pagoPostArmado) "Enviar Lista a la Tienda" // 🚀 MAGIA DEL RETAIL
                                else "Confirmar y Pagar",
                                fontWeight = FontWeight.Bold,
                                fontSize = 16.sp,
                                color = Color.White
                            )
                        }
                    }
                }
            }
        ) { padding ->
            Column(modifier = Modifier.fillMaxSize().padding(padding).verticalScroll(rememberScrollState()).padding(16.dp)) {

                if (permiteDelivery || permitePickup) {
                    Text("¿Cómo quieres recibirlo?", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoPrincipal)
                    Spacer(Modifier.height(12.dp))
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        if (permiteDelivery) BotonSeleccion("A Domicilio", Icons.Default.TwoWheeler, tipoPedido == "Delivery", { tipoPedido = "Delivery" }, Modifier.weight(1f), isDark)
                        if (permitePickup) BotonSeleccion("Pasar por él", Icons.Default.Storefront, tipoPedido == "Pickup", { tipoPedido = "Pickup" }, Modifier.weight(1f), isDark)
                    }
                    Spacer(Modifier.height(24.dp))
                } else {
                    Card(modifier = Modifier.fillMaxWidth().padding(bottom = 24.dp), colors = CardDefaults.cardColors(containerColor = Color(0xFFFFEBEE))) {
                        Text("El restaurante actualmente no tiene métodos de entrega activos.", modifier = Modifier.padding(16.dp), color = Color.Red, fontSize = 14.sp, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center)
                    }
                }

                val permiteASAPOpcion = if (tipoPedido == "Delivery") permiteDeliveryASAP else (estaAbiertoAhora && !estaEnPanico)

                Text("¿Cuándo lo necesitas?", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoPrincipal)
                Spacer(Modifier.height(12.dp))
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    if (permiteASAPOpcion) {
                        BotonSeleccion("Lo antes posible", Icons.Default.Bolt, tipoTiempo == "ASAP", { tipoTiempo = "ASAP" }, Modifier.weight(1f), isDark)
                    }
                    if (permiteProg) {
                        BotonSeleccion("Programar", Icons.Default.CalendarToday, tipoTiempo == "Programado", { tipoTiempo = "Programado" }, Modifier.weight(1f), isDark)
                    }
                }

                AnimatedVisibility(visible = tipoTiempo == "Programado" && permiteProg) {
                    Card(modifier = Modifier.fillMaxWidth().padding(top = 16.dp), colors = CardDefaults.cardColors(containerColor = colorFondoTarjeta), border = BorderStroke(1.dp, colorBordeCard)) {
                        Column(modifier = Modifier.padding(16.dp)) {
                            Text("Día", fontWeight = FontWeight.Bold, fontSize = 13.sp, color = colorTextoSecundario)
                            Row(modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                ChipSeleccion(hoyStr, diaProgramado == hoyStr, isDark) { diaProgramado = hoyStr }
                                ChipSeleccion(mananaStr, diaProgramado == mananaStr, isDark) { diaProgramado = mananaStr }
                            }
                            Spacer(Modifier.height(8.dp))
                            Text("Hora estimada", fontWeight = FontWeight.Bold, fontSize = 13.sp, color = colorTextoSecundario)

                            if (horasDisponibles.isEmpty()) {
                                Text("No hay horarios disponibles para este día.", color = Color.Red, fontSize = 13.sp, modifier = Modifier.padding(vertical = 8.dp))
                            } else {
                                Row(modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp).horizontalScroll(rememberScrollState()), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    horasDisponibles.forEach { ChipSeleccion(it, horaProgramada == it, isDark) { horaProgramada = it } }
                                }
                            }
                        }
                    }
                }

                Spacer(Modifier.height(24.dp))

                // 🚀 COLORES ADAPTADOS AL DARK MODE PARA EVITAR EL DESTELLO BLANCO
                val colorErrorFondo = if (isDark) Color(0xFF3B1A1E) else Color(0xFFFFEBEE)
                val colorErrorBorde = if (isDark) Color(0xFFD32F2F).copy(alpha = 0.5f) else Color.Red.copy(alpha = 0.5f)

                AnimatedContent(
                    targetState = tipoPedido,
                    transitionSpec = {
                        (fadeIn() + expandVertically()) togetherWith (fadeOut() + shrinkVertically())
                    },
                    label = "transicion_entrega"
                ) { tipo ->
                    when (tipo) {
                        "Delivery" -> {
                            Column {
                                Text("Dirección de entrega", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoPrincipal)
                                Spacer(Modifier.height(12.dp))
                                Card(
                                    onClick = { cambiarDir = true },
                                    enabled = true, // Siempre habilitado para que puedan cambiar
                                    modifier = Modifier.fillMaxWidth(),
                                    // 🚀 AQUI CORREGIMOS EL DESTELLO BLANCO USANDO colorErrorFondo
                                    colors = CardDefaults.cardColors(containerColor = if(!coordenadasValidas || fueraDeRango) colorErrorFondo else colorFondoTarjeta),
                                    border = BorderStroke(1.dp, if(!coordenadasValidas || fueraDeRango) colorErrorBorde else colorBordeCard)
                                ) {
                                    Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                                        // 🚀 ÍCONO INTELIGENTE
                                        val iconoMostrar = when (tipoUbicacionCheckout.lowercase()) {
                                            "casa" -> Icons.Default.Home
                                            "trabajo" -> Icons.Default.Work
                                            else -> Icons.Default.Place
                                        }
                                        Icon(iconoMostrar, null, tint = RosaYendo, modifier = Modifier.size(28.dp))

                                        Spacer(Modifier.width(12.dp))
                                        Column(modifier = Modifier.weight(1f)) {
                                            // 🚀 LÓGICA PARA MOSTRAR EL TÍTULO "OTRO" (Ej: "Casa de mis suegros")
                                            val textoTitulo = if (!coordenadasValidas) {
                                                "Ajusta tu ubicación"
                                            } else if (tipoUbicacionCheckout.equals("Otro", ignoreCase = true) && tituloCheckout.isNotBlank()) {
                                                tituloCheckout
                                            } else {
                                                tipoUbicacionCheckout.ifBlank { "Dirección" }
                                            }

                                            Text(
                                                text = textoTitulo,
                                                fontWeight = FontWeight.Bold,
                                                fontSize = 16.sp,
                                                color = if(!coordenadasValidas) Color.Red else colorTextoPrincipal
                                            )
                                            Text(direccionCheckout, color = colorTextoSecundario, fontSize = 13.sp, maxLines = 1)
                                            if (opcionEntregaCheckout.isNotBlank() || indicacionesCheckout.isNotBlank()) {
                                                Text(
                                                    text = "$opcionEntregaCheckout ${if(indicacionesCheckout.isNotBlank()) "• $indicacionesCheckout" else ""}",
                                                    color = RosaYendo,
                                                    fontSize = 11.sp,
                                                    fontWeight = FontWeight.Bold,
                                                    modifier = Modifier.padding(top = 4.dp)
                                                )
                                            }
                                        }
                                        Icon(Icons.Default.ChevronRight, null, tint = colorTextoSecundario)
                                    }
                                }

                                if (fueraDeRango) {
                                    Card(modifier = Modifier.padding(top = 8.dp).fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = colorErrorFondo)) {
                                        Text("Estás a ${com.yendo.mx.utils.formatDecimal(distanciaActual, 1)} km. El límite es de $kmMaximos km.", modifier = Modifier.padding(12.dp), color = Color.Red, fontSize = 12.sp, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center)
                                    }
                                }
                                Spacer(Modifier.height(24.dp))
                            }
                        }
                        "Pickup" -> {
                            Column {
                                Text("Recoger en", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoPrincipal)
                                Spacer(Modifier.height(12.dp))
                                Card(
                                    modifier = Modifier.fillMaxWidth(),
                                    colors = CardDefaults.cardColors(containerColor = colorFondoTarjeta),
                                    border = BorderStroke(1.dp, colorBordeCard)
                                ) {
                                    Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                                        Icon(Icons.Default.Storefront, null, tint = RosaYendo)
                                        Spacer(Modifier.width(12.dp))
                                        Column(modifier = Modifier.weight(1f)) {
                                            Text(CartManager.restauranteActualNombre ?: "Restaurante", fontWeight = FontWeight.Bold, fontSize = 15.sp, color = colorTextoPrincipal)
                                            Text(CartManager.restauranteActualDireccion ?: "Dirección no disponible", color = colorTextoSecundario, fontSize = 13.sp)
                                        }
                                    }
                                }
                                Spacer(Modifier.height(24.dp))
                            }
                        }
                    }
                }

                Text("¿Cómo quieres pagar?", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoPrincipal)
                Spacer(Modifier.height(12.dp))
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    if (metodosFinales.contains("Efectivo")) MetodoPagoItem("Efectivo", Icons.Default.Payments, metodoPago == "Efectivo", Modifier.weight(1f), isDark) { metodoPago = "Efectivo" }
                    
                    // 🚀 BOTÓN DE TARJETA INTELIGENTE
                    if (metodosFinales.contains("Tarjeta")) {
                        val textoTarjeta = if (tarjetaSeleccionada != null) "••• ${tarjetaSeleccionada!!.terminacion}" else "Tarjeta"
                        MetodoPagoItem(textoTarjeta, Icons.Default.CreditCard, metodoPago == "Tarjeta", Modifier.weight(1f), isDark) { 
                            metodoPago = "Tarjeta"
                            mostrarSelectorTarjetas = true 
                        }
                    }
                    
                    if (metodosFinales.contains("Transferencia")) MetodoPagoItem("Transfer", Icons.Default.AccountBalance, metodoPago == "Transferencia", Modifier.weight(1f), isDark) { metodoPago = "Transferencia" }
                }

                // 🚀 ADVERTENCIA SI NO HAY TARJETAS
                AnimatedVisibility(visible = metodoPago == "Tarjeta" && tarjetasGuardadas.isEmpty()) {
                    Text("No tienes tarjetas guardadas.", color = colorTextoSecundario, fontSize = 12.sp, modifier = Modifier.padding(top = 8.dp))
                }

                AnimatedVisibility(visible = metodoPago == "Transferencia") {
                    Card(modifier = Modifier.padding(top = 16.dp).fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = if(isDark) Color(0xFF0D253F) else Color(0xFFF0F7FF)), border = BorderStroke(1.dp, Color(0xFF1976D2).copy(alpha = 0.3f))) {
                        Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Default.Info, null, tint = Color(0xFF42A5F5), modifier = Modifier.size(24.dp))
                            Spacer(Modifier.width(12.dp))
                            Column {
                                Text("Pago Protegido", fontWeight = FontWeight.Bold, color = Color(0xFF42A5F5))
                                Text("Te daremos la cuenta a depositar una vez que el restaurante confirme.", fontSize = 12.sp, color = colorTextoSecundario)
                            }
                        }
                    }
                }

                Spacer(Modifier.height(24.dp))

                AnimatedVisibility(visible = tipoPedido == "Delivery") {
                    Column(modifier = Modifier.padding(bottom = 24.dp)) {
                        Text("Propina para el repartidor", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoPrincipal)
                        Text("El 100% de la propina es para el repartidor.", color = colorTextoSecundario, fontSize = 12.sp)
                        Spacer(Modifier.height(12.dp))

                        val opcionesPropina = remember(total) {
                            val generadas = generarOpcionesPropina(total)
                            val ordenadas = generadas.partition { it == propinaRecomendada }
                            ordenadas.first + ordenadas.second
                        }

                        LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            items(opcionesPropina) { monto ->
                                val isSelected = propinaSeleccionada == monto
                                val isRecomendada = monto == propinaRecomendada && monto != 0.0

                                OutlinedButton(
                                    onClick = { propinaSeleccionada = monto },
                                    modifier = Modifier.height(54.dp),
                                    colors = ButtonDefaults.outlinedButtonColors(
                                        containerColor = if (isSelected) RosaYendo.copy(alpha = 0.1f) else colorFondoTarjeta,
                                        contentColor = if (isSelected) RosaYendo else colorTextoSecundario
                                    ),
                                    border = BorderStroke(1.dp, if (isSelected) RosaYendo else colorBordeCard),
                                    shape = RoundedCornerShape(12.dp)
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                        Text(if(monto == 0.0) "Nada" else "$${monto.toInt()}", fontWeight = FontWeight.Bold)
                                        if (isRecomendada) Text("Sugerido", fontSize = 9.sp)
                                    }
                                }
                            }
                        }
                    }
                }

                Text("Descuentos", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoPrincipal)
                Spacer(Modifier.height(8.dp))
                Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    OutlinedTextField(
                        value = cuponInput,
                        onValueChange = { cuponInput = it.uppercase() },
                        placeholder = { Text("Ingresa tu cupón", color = colorTextoSecundario) },
                        modifier = Modifier.weight(1f).height(56.dp),
                        shape = RoundedCornerShape(12.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = RosaYendo,
                            unfocusedBorderColor = colorBordeCard,
                            focusedTextColor = colorTextoPrincipal,
                            unfocusedTextColor = colorTextoPrincipal
                        ),
                        singleLine = true
                    )
                    Spacer(Modifier.width(12.dp))
                    Button(
                        onClick = {
                            scope.launch {
                                if (cuponInput.isBlank()) {
                                    mensajeCupon = "Ingresa un código"
                                    descuentoAplicado = 0.0
                                    patrocinadorCupon = ""
                                    return@launch
                                }

                                mensajeCupon = "Validando..."

                                try {
                                    // 🚀 1. VAMOS DIRECTO A FIREBASE (Búsqueda por ID del documento)
                                    val snapshot = Firebase.firestore
                                        .collection("Cupones")
                                        .document(cuponInput)
                                        .get()

                                    if (!snapshot.exists) {
                                        descuentoAplicado = 0.0
                                        patrocinadorCupon = ""
                                        mensajeCupon = "Cupón no existe o está mal escrito."
                                        return@launch
                                    }

                                    // 🚀 2. MAPEAMOS LOS DATOS
                                    val cuponInfo = snapshot.data<com.yendo.mx.models.CuponFirebase>()

                                    // 🚀 3. INICIA LA AUDITORÍA DE SEGURIDAD DEL CUPÓN
                                    val zonaActual = zonaConfig?.id_zona

                                    if (!cuponInfo.activo) {
                                        descuentoAplicado = 0.0
                                        mensajeCupon = "Este cupón está desactivado o ya expiró."
                                        return@launch
                                    }

                                    // 🚀 VALIDACIÓN DE MÓDULO (SEGMENTACIÓN DE CUPONES)
                                    // Si la lista no está vacía, DEBE contener el módulo actual para funcionar aquí.
                                    val tipoVertical = CartManager.restauranteActualTipoNegocio ?: "RESTAURANTE"
                                    if (cuponInfo.modulosValidos.isNotEmpty() && !cuponInfo.modulosValidos.contains(tipoVertical)) {
                                        descuentoAplicado = 0.0
                                        mensajeCupon = "Este cupón no aplica para esta sección."
                                        return@launch
                                    }

                                    if (total < cuponInfo.consumoMinimo) {
                                        descuentoAplicado = 0.0
                                        mensajeCupon = "Requiere consumo mínimo de $${cuponInfo.consumoMinimo}"
                                        return@launch
                                    }
                                    if (cuponInfo.metodosValidos.isNotEmpty() && !cuponInfo.metodosValidos.contains(metodoPago)) {
                                        descuentoAplicado = 0.0
                                        mensajeCupon = "Aplica pagando con: ${cuponInfo.metodosValidos.joinToString(", ")}"
                                        return@launch
                                    }
                                    if (cuponInfo.idRestauranteExclusivo.isNotBlank() && CartManager.restauranteActualId != cuponInfo.idRestauranteExclusivo) {
                                        descuentoAplicado = 0.0
                                        mensajeCupon = "Este cupón no aplica en este local."
                                        return@launch
                                    }
                                    if (cuponInfo.idZonaExclusiva.isNotBlank() && zonaActual != cuponInfo.idZonaExclusiva) {
                                        descuentoAplicado = 0.0
                                        mensajeCupon = "Este cupón no es válido para tu ciudad."
                                        return@launch
                                    }
                                    if (tipoPedido == "Delivery" && !cuponInfo.aplicaDelivery) {
                                        descuentoAplicado = 0.0
                                        mensajeCupon = "Este cupón es exclusivo para 'Pasar por él'."
                                        return@launch
                                    }
                                    if (tipoPedido == "Pickup" && !cuponInfo.aplicaPickup) {
                                        descuentoAplicado = 0.0
                                        mensajeCupon = "Este cupón es exclusivo para envíos a domicilio."
                                        return@launch
                                    }

                                    if (cuponInfo.usosActuales >= cuponInfo.limiteUsosGlobal) {
                                        descuentoAplicado = 0.0
                                        mensajeCupon = "¡Ups! Este cupón se ha agotado."
                                        return@launch
                                    }

// 🚀 3.2 NUEVA VALIDACIÓN: LÍMITE POR USUARIO (¿Ya lo usó mucho este cliente?)
                                    val uid = com.yendo.mx.repository.AuthRepository.usuarioActual.value?.uid
                                    if (uid != null) {
                                        // Buscamos en la base de datos cuántos pedidos tiene este usuario con ESTE cupón
                                        val usosPrevios = Firebase.firestore.collection("Pedidos")
                                            .where { "idUsuario" equalTo uid }
                                            .where { "idCuponUsado" equalTo cuponInput } // El texto que escribió en el TextField
                                            .get()

                                        if (usosPrevios.documents.size >= cuponInfo.limitePorUsuario) {
                                            descuentoAplicado = 0.0
                                            mensajeCupon = "Ya has usado este cupón el máximo de veces permitido."
                                            return@launch
                                        }
                                    }

                                    // 🚀 4. REGLA DEL PRIMER PEDIDO
                                    if (cuponInfo.soloPrimerPedido) {
                                        val uid = com.yendo.mx.repository.AuthRepository.usuarioActual.value?.uid
                                        if (uid != null) {
                                            // Buscamos si el usuario ya tiene un pedido entregado o en curso
                                            val pedidosAnteriores = Firebase.firestore.collection("Pedidos")
                                                .where { "idCliente" equalTo uid }
                                                .get()

                                            if (pedidosAnteriores.documents.isNotEmpty()) {
                                                descuentoAplicado = 0.0
                                                mensajeCupon = "Este cupón es exclusivo para tu primera compra."
                                                return@launch
                                            }
                                        }
                                    }

                                    // 🚀 5. APLICAR MATEMÁTICAS SEGÚN EL TIPO
                                    descuentoAplicado = when (cuponInfo.tipo.uppercase()) {
                                        "CANTIDAD" -> cuponInfo.valor
                                        "PORCENTAJE" -> total * (cuponInfo.valor / 100.0)
                                        "ENVIO" -> costoEnvioReal
                                        else -> 0.0
                                    }

                                    // Validamos que el descuento no sea mayor al total de los productos
                                    if (descuentoAplicado > total && cuponInfo.tipo.uppercase() != "ENVIO") {
                                        descuentoAplicado = total
                                    }

                                    patrocinadorCupon = cuponInfo.quienPaga
                                    mensajeCupon = "¡Cupón aplicado! -$${formatPrecio(descuentoAplicado)}"

                                } catch (e: Exception) {
                                    descuentoAplicado = 0.0
                                    patrocinadorCupon = ""
                                    mensajeCupon = "Error de red al validar."
                                }
                            }
                        },
                        modifier = Modifier.height(56.dp),
                        colors = ButtonDefaults.buttonColors(if(isDark) Color(0xFF444444) else Color(0xFF333333)),
                        shape = RoundedCornerShape(12.dp)
                    ) { Text("Aplicar", fontWeight = FontWeight.Bold, color = Color.White) }
                }
                if (mensajeCupon.isNotEmpty()) {
                    Text(mensajeCupon, color = if (descuentoAplicado > 0) Color(0xFF00C853) else Color.Red, fontSize = 12.sp, modifier = Modifier.padding(top = 8.dp, start = 4.dp))
                }

                Spacer(Modifier.height(24.dp))

                AnimatedVisibility(visible = metodoPago == "Efectivo") {
                    Card(modifier = Modifier.fillMaxWidth().padding(bottom = 24.dp), colors = CardDefaults.cardColors(containerColor = RosaYendo.copy(alpha = 0.05f)), border = BorderStroke(2.dp, RosaYendo.copy(alpha = 0.3f))) {
                        Column(modifier = Modifier.padding(16.dp)) {
                            OpcionesPagoEfectivo(totalPagar, cambioEfectivoSeleccionado, isDark, { cambioEfectivoSeleccionado = it })
                        }
                    }
                }

                Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = colorFondoTarjeta), border = BorderStroke(1.dp, colorBordeCard)) {
                    Column(modifier = Modifier.padding(20.dp)) {
                        Text("Resumen", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = colorTextoPrincipal)
                        Spacer(Modifier.height(12.dp))
                        ResumenFila("Subtotal", "$${formatPrecio(total)}", colorTextoSecundario, colorTextoPrincipal)

                        // 🚀 NUEVO: Mostramos la tarifa de servicio si es mayor a 0
                        // 🚀 NUEVO: Fila de Tarifa de Servicio Interactiva
                        if (tarifaServicio > 0.0) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(vertical = 4.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    modifier = Modifier
                                        .clip(RoundedCornerShape(4.dp))
                                        .clickable { mostrarDialogoTarifa = true }
                                        .padding(end = 8.dp, top = 2.dp, bottom = 2.dp)
                                ) {
                                    Text("Tarifa de servicio", color = colorTextoSecundario, fontSize = 14.sp)
                                    Spacer(Modifier.width(4.dp))
                                    Icon(Icons.Default.Info, contentDescription = "Info Tarifa", tint = colorTextoSecundario, modifier = Modifier.size(14.dp))
                                }
                                Text("$${formatPrecio(tarifaServicio)}", color = colorTextoPrincipal, fontSize = 14.sp)
                            }
                        }
                        if (tipoPedido == "Delivery") {
                            val labelEnvio = if (!coordenadasValidas) "Envío" else "Envío (${com.yendo.mx.utils.formatDecimal(distanciaActual, 1)} km)"
                            val textoCosto = if (costoEnvio == -1.0) "Calculando..." else if (!coordenadasValidas) "Pendiente GPS" else if (fueraDeRango) "No Disponible" else if (costoEnvio == 0.0) "Gratis" else "$${formatPrecio(costoEnvio)}"
                            ResumenFila(labelEnvio, textoCosto, colorTextoSecundario, colorTextoPrincipal)
                        }
                        if (propinaSeleccionada > 0) ResumenFila("Propina", "$${formatPrecio(propinaSeleccionada)}", colorTextoSecundario, colorTextoPrincipal)
                        if (descuentoAplicado > 0) {
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                Text("Descuento", color = Color(0xFF00C853), fontSize = 14.sp)
                                Text("-$${formatPrecio(descuentoAplicado)}", color = Color(0xFF00C853), fontSize = 14.sp, fontWeight = FontWeight.Bold)
                            }
                        }
                        HorizontalDivider(modifier = Modifier.padding(vertical = 12.dp), color = colorBordeCard)
                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                            Text(if (pagoPostArmado) "Total Estimado" else "Total", fontWeight = FontWeight.Black, fontSize = 18.sp, color = colorTextoPrincipal)
                            Text(if (pagoPostArmado) "~ $${formatPrecio(totalPagar)}" else "$${formatPrecio(totalPagar)}", fontWeight = FontWeight.ExtraBold, fontSize = 22.sp, color = RosaYendo)
                        }
                    }
                }
                Spacer(Modifier.height(100.dp))
            }
        }
    }

    if (cambiarDir) {
        SelectorDireccionSheet(
            listaDirecciones = listaDirecciones,
            onDismiss = { cambiarDir = false },
            onDireccionSeleccionada = { dir ->
                direccionCheckout = dir.direccion
                latitudCheckout = dir.lat
                longitudCheckout = dir.lng
                tipoUbicacionCheckout = dir.tipoUbicacion
                opcionEntregaCheckout = dir.opcionEntrega
                indicacionesCheckout = dir.indicaciones
                tituloCheckout = dir.titulo // 🚀 Y AQUÍ TAMBIÉN

                scope.launch { DireccionesRepository.guardarDireccion(dir.copy(ultimaVezUsada = getAhora())) }
                cambiarDir = false
            },
            onUsarUbicacionActual = { forzarGpsCheckout = true; mostrarMapaCheckout = true; cambiarDir = false },
            onAbrirMapa = { indice, dir -> forzarGpsCheckout = false; indiceEdicion = indice; direccionPrevia = dir.direccion; mostrarMapaCheckout = true; cambiarDir = false },
            onEliminarDireccion = { dir -> scope.launch { DireccionesRepository.eliminarDireccion(dir.id) } }
        )
    }

    // 🚀 DIÁLOGO ESTILO UBER EATS / DIDI FOOD
    if (mostrarDialogoTarifa) {
        AlertDialog(
            onDismissRequest = { mostrarDialogoTarifa = false },
            title = {
                Text("Tarifa de servicio", fontWeight = FontWeight.Black, fontSize = 18.sp, color = colorTextoPrincipal)
            },
            text = {
                Text(
                    text = "Esta tarifa nos ayuda a mantener nuestra plataforma, ofrecer soporte en tiempo real y mejorar continuamente la experiencia de Yendo MX para ti.\n\nSe calcula con base en el subtotal de tu pedido.",
                    color = colorTextoSecundario,
                    fontSize = 14.sp,
                    lineHeight = 20.sp
                )
            },
            confirmButton = {
                TextButton(onClick = { mostrarDialogoTarifa = false }) {
                    Text("Entendido", color = RosaYendo, fontWeight = FontWeight.Bold, fontSize = 15.sp)
                }
            },
            containerColor = colorFondoTarjeta,
            shape = RoundedCornerShape(16.dp),
            tonalElevation = 8.dp
        )
    }

    // =========================================================================
    // 🚀 BOTTOM SHEET: SELECTOR DE TARJETAS INTELIGENTE
    // =========================================================================
    var mostrarDialogoAgregarTarjeta by rememberSaveable { mutableStateOf(false) }

    if (mostrarSelectorTarjetas) {
        ModalBottomSheet(
            onDismissRequest = { mostrarSelectorTarjetas = false },
            containerColor = colorFondoTarjeta,
            shape = RoundedCornerShape(topStart = 24.dp, topEnd = 24.dp)
        ) {
            Column(modifier = Modifier.fillMaxWidth().padding(horizontal = 20.dp, vertical = 8.dp)) {
                Text("Selecciona una tarjeta", fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = colorTextoPrincipal, modifier = Modifier.padding(bottom = 16.dp))

                LazyRow(
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    contentPadding = PaddingValues(bottom = 16.dp)
                ) {
                    items(tarjetasGuardadas) { tarjeta ->
                        val isSelected = tarjetaSeleccionada?.id == tarjeta.id
                        val (marcaColor, marcaBg) = when(tarjeta.marca.lowercase()) {
                            "visa" -> Pair(Color.White, Color(0xFF1A237E))
                            "mastercard" -> Pair(Color.White, Color(0xFFB71C1C))
                            "amex" -> Pair(Color.White, Color(0xFF00695C))
                            else -> Pair(Color.White, Color.DarkGray)
                        }

                        Card(
                            onClick = {
                                tarjetaSeleccionada = tarjeta
                                mostrarSelectorTarjetas = false
                            },
                            modifier = Modifier.width(180.dp).height(100.dp),
                            colors = CardDefaults.cardColors(containerColor = if (isSelected) RosaYendo else colorFondoPantalla),
                            border = BorderStroke(1.dp, if (isSelected) RosaYendo else colorBordeCard),
                            shape = RoundedCornerShape(16.dp)
                        ) {
                            Column(modifier = Modifier.padding(12.dp).fillMaxSize(), verticalArrangement = Arrangement.SpaceBetween) {
                                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                    Box(modifier = Modifier.clip(RoundedCornerShape(4.dp)).background(if (isSelected) Color.White.copy(alpha = 0.2f) else marcaBg).padding(horizontal = 6.dp, vertical = 2.dp)) {
                                        Text(tarjeta.marca, color = if (isSelected) Color.White else marcaColor, fontSize = 10.sp, fontWeight = FontWeight.Black)
                                    }
                                    if (isSelected) Icon(Icons.Default.CheckCircle, null, tint = Color.White, modifier = Modifier.size(20.dp))
                                }
                                Text("•••• ${tarjeta.terminacion}", color = if (isSelected) Color.White else colorTextoPrincipal, fontWeight = FontWeight.Bold, fontSize = 16.sp, letterSpacing = 2.sp)
                            }
                        }
                    }
                }

                HorizontalDivider(color = colorBordeCard, modifier = Modifier.padding(vertical = 12.dp))

                // BOTÓN PARA AGREGAR NUEVA
                Row(
                    modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(12.dp)).clickable { mostrarDialogoAgregarTarjeta = true }.padding(vertical = 16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(modifier = Modifier.size(40.dp).background(RosaYendo.copy(alpha = 0.1f), CircleShape), contentAlignment = Alignment.Center) {
                        Icon(Icons.Default.Add, null, tint = RosaYendo)
                    }
                    Spacer(Modifier.width(16.dp))
                    Text("Agregar nueva tarjeta", fontWeight = FontWeight.Bold, color = RosaYendo, fontSize = 16.sp)
                }
                Spacer(Modifier.height(30.dp).navigationBarsPadding())
            }
        }
    }

    // 🚀 DIÁLOGO DE NUEVA TARJETA (Reusando tu componente del Perfil)
    if (mostrarDialogoAgregarTarjeta) {
        com.yendo.mx.ui.profile.DialogoNuevaTarjeta(
            isDark = isDark,
            onDismiss = { mostrarDialogoAgregarTarjeta = false },
            onGuardar = { nuevaTarjeta ->
                scope.launch {
                    try {
                        val uid = usuarioId ?: return@launch
                        val docRef = dev.gitlive.firebase.Firebase.firestore
                            .collection("Usuarios")
                            .document(uid)
                            .collection("Tarjetas")
                            .document

                        // 🚀 PASO 2: Guardamos en Firebase con el ID Persistente que ya viene en nuevaTarjeta
                        val tarjetaFinal = nuevaTarjeta.copy(id = docRef.id)
                        docRef.set(tarjetaFinal)

                        tarjetasGuardadas.add(tarjetaFinal)
                        tarjetaSeleccionada = tarjetaFinal

                        mostrarDialogoAgregarTarjeta = false
                        mostrarSelectorTarjetas = false
                    } catch (e: Exception) {
                        println("YENDO_ERROR: Falló al guardar - ${e.message}")
                    }
                }
            }
        )
    }

    if (mostrarAlertaDireccionLejana) {
        AlertDialog(
            onDismissRequest = { mostrarAlertaDireccionLejana = false },
            title = {
                Text("📍 ¿Dirección correcta?", fontWeight = FontWeight.Bold, color = colorTextoPrincipal)
            },
            text = {
                Text("Detectamos que estás lejos de la dirección de entrega seleccionada ($direccionCheckout). \n\n¿Seguro que deseas enviar tu pedido ahí?", color = colorTextoSecundario)
            },
            confirmButton = {
                Button(
                    onClick = {
                        mostrarAlertaDireccionLejana = false
                        if (metodoPago == "Tarjeta" && tarjetaSeleccionada != null) {
                            val tokenActual = tarjetaSeleccionada?.tokenPago ?: ""
                            if (tokenActual.length < 25) {
                                mostrarDialogoCVV = true
                            } else {
                                realizarPedido(null, null)
                            }
                        } else {
                            realizarPedido(null, null)
                        }
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = RosaYendo)
                ) {
                    Text("Sí, enviar ahí", color = Color.White)
                }
            },
            dismissButton = {
                OutlinedButton(
                    onClick = {
                        mostrarAlertaDireccionLejana = false
                        cambiarDir = true
                    }
                ) {
                    Text("Cambiar dirección", color = colorTextoPrincipal)
                }
            },
            containerColor = colorFondoTarjeta,
            shape = RoundedCornerShape(16.dp)
        )
    }

    if (mostrarDialogoCVV) {
        AlertDialog(
            onDismissRequest = { if (!procesandoCVV) mostrarDialogoCVV = false },
            title = { Text("Verificación de Seguridad", color = colorTextoPrincipal) },
            text = {
                Column {
                    Text("Ingresa el CVV (3 o 4 dígitos) de tu tarjeta terminada en ${tarjetaSeleccionada?.terminacion}.", fontSize = 14.sp, color = colorTextoSecundario, modifier = Modifier.padding(bottom = 16.dp))
                    OutlinedTextField(
                        value = cvvInput,
                        onValueChange = { if(it.length <= 4 && it.all { char -> char.isDigit() }) cvvInput = it },
                        label = { Text("CVV") },
                        modifier = Modifier.fillMaxWidth(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.NumberPassword),
                        colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal),
                        enabled = !procesandoCVV
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = { procesarPagoConCVV() },
                    colors = ButtonDefaults.buttonColors(containerColor = RosaYendo),
                    enabled = cvvInput.length >= 3 && !procesandoCVV
                ) {
                    if (procesandoCVV) CircularProgressIndicator(color = Color.White, modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
                    else Text("Confirmar", color = Color.White)
                }
            },
            dismissButton = {
                TextButton(onClick = { mostrarDialogoCVV = false }, enabled = !procesandoCVV) {
                    Text("Cancelar", color = RosaYendo)
                }
            },
            containerColor = colorFondoTarjeta
        )
    }
}



// FUNCIONES AUXILIARES
fun calcularDistanciaMetros(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Float {
    val r = 6371000.0 // Radio de la Tierra en metros
    val dLat = (lat2 - lat1) * (PI / 180.0)
    val dLon = (lon2 - lon1) * (PI / 180.0)
    val a = sin(dLat / 2) * sin(dLat / 2) +
            cos(lat1 * (PI / 180.0)) * cos(lat2 * (PI / 180.0)) *
            sin(dLon / 2) * sin(dLon / 2)
    val c = 2 * atan2(sqrt(a), sqrt(1 - a))
    return (r * c).toFloat()
}

fun calcularDistanciaKm(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Double {
    if (lat1 == 0.0 || lon1 == 0.0 || lat2 == 0.0 || lon2 == 0.0) return 0.0
    val r = 6371.0
    val dLat = (lat2 - lat1) * PI / 180.0
    val dLon = (lon2 - lon1) * PI / 180.0
    val a = sin(dLat / 2) * sin(dLat / 2) + cos(lat1 * PI / 180.0) * cos(lat2 * PI / 180.0) * sin(dLon / 2) * sin(dLon / 2)
    return r * (2 * atan2(sqrt(a), sqrt(1 - a)))
}

fun calcularCostoEnvioDin(distanciaLineaRectaKm: Double, tarifaInicial: Double, costoPorKm: Double, envioMinimo: Double): Double {
    return maxOf(envioMinimo, tarifaInicial + (distanciaLineaRectaKm * costoPorKm))
}

fun generarOpcionesPropina(subtotal: Double): List<Double> {
    val opciones = mutableSetOf(0.0, 5.0, 10.0, 15.0, 20.0)
    opciones.addAll(listOf(kotlin.math.round(subtotal * 0.10), kotlin.math.round(subtotal * 0.15), kotlin.math.round(subtotal * 0.20)))
    return opciones.filter { it <= 100.0 }.sorted()
}

